/**
 * format.ts — pure money/percent/number formatting for investor numbers.
 *
 * MOVED from:
 *   web-app/src/ui.ts             (formatPrice, formatPriceShort)
 *   web-app/src/investorCard.ts    (fmtPct — 1-decimal card variant)
 *   web-app/src/investorDetail.ts  (fmtMoneyCompact)
 *
 * All pure functions — Intl and Math only, no DOM. Null/undefined inputs
 * render as '—' or '$—' per the originals, never as 0.
 */

/** $350,000 — whole dollars, USD. Null → $0 (verbatim web-app behavior). */
export function formatPrice(n: number | null | undefined): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n ?? 0);
}

/** Currency alias — same USD whole-dollar formatting as formatPrice. */
export const formatCurrency: (n: number | null | undefined) => string = formatPrice;

/**
 * Compact price for space-constrained contexts (map markers, chips).
 *   < $1M       → "$350K"
 *   < $10M      → "$1.2M"  (one decimal, "$1M" when whole)
 *   ≥ $10M      → "$12M"   (no decimal)
 *   null / ≤0   → "$—"
 * Matches the Zillow/Redfin price-pill convention.
 */
export function formatPriceShort(n: number | null | undefined): string {
  if (n == null || n <= 0) return '$—';
  return '$' + compactMagnitude(n, { millionDecimals: 1, trimMillionZero: true, tenMillionWhole: true });
}

/**
 * Round-then-pick-unit compact magnitude (no sign, no "$").
 *
 * The unit is chosen AFTER rounding to that unit's precision, so a value
 * that rounds up to the next unit is printed in the next unit:
 * 999,600 → "1M", never "1000K"; 9,999,999 with tenMillionWhole → "10M".
 * Every compact-money formatter in shared/ and the apps routes through
 * here so the same number never reads "$1000K" on one tile and "$1.0M"
 * on the next.
 */
export function compactMagnitude(
  value: number,
  opts: {
    /** Decimals when printing millions (default 1). */
    millionDecimals?: number;
    /** Print "$1M" instead of "$1.0M" when the decimals are all zero. */
    trimMillionZero?: boolean;
    /** ≥ $10M prints without decimals ("$12M"). */
    tenMillionWhole?: boolean;
    /** Below this, print the whole number instead of "K" (default 1,000). */
    thousandFloor?: number;
  } = {},
): string {
  const abs = Math.abs(value);
  const md = opts.millionDecimals ?? 1;
  const kFloor = opts.thousandFloor ?? 1_000;
  const thousands = Math.round(abs / 1_000);
  if (abs < kFloor && thousands < 1_000) {
    return Math.round(abs).toLocaleString('en-US');
  }
  if (thousands < 1_000) {
    return `${thousands}K`;
  }
  const millions = abs / 1_000_000;
  if (opts.tenMillionWhole && Math.round(millions * 10 ** md) / 10 ** md >= 10) {
    return `${Math.round(millions)}M`;
  }
  const fixed = millions.toFixed(md);
  if (opts.trimMillionZero && Number(fixed) % 1 === 0) return `${Number(fixed).toFixed(0)}M`;
  return `${fixed}M`;
}

/**
 * Signed compact money: +$45K / −$12K / $1.20M. Used for cash-flow
 * tiles where the sign is the signal. Null → '—'.
 * (Moved from investorCard.ts fmtMoney.)
 */
export function formatSignedMoney(value: number | null | undefined): string {
  if (value == null) return '—';
  const sign = value >= 0 ? '+' : '−';
  const abs = Math.abs(Math.round(value));
  return `${sign}$${abs.toLocaleString()}`;
}

/**
 * Unsigned compact money for projection columns: $1.20M / $45K / $800.
 * Negatives use the − prefix. (Moved from investorDetail.ts
 * fmtMoneyCompact.)
 */
export function formatMoneyCompact(n: number): string {
  const sign = n < 0 ? '−' : '';
  return `${sign}$${compactMagnitude(n, { millionDecimals: 2 })}`;
}

/**
 * 4.42% — percent with configurable decimals (2 by default, matching
 * the deal page's metric display). Null → '—'.
 */
export function formatPct(value: number | null | undefined, decimals = 2): string {
  if (value == null) return '—';
  return `${Number(value).toFixed(decimals)}%`;
}

/**
 * Multiple / ratio display: 11.90, 1.25. Null → '—'.
 * (GRM, DSCR, price-to-rent share this shape.)
 */
export function formatMultiple(value: number | null | undefined): string {
  if (value == null) return '—';
  return Number(value).toFixed(2);
}
